/* ------------------------------------------------------------------
   Cart sidecart — self-contained drawer + cart API wiring.
   Reference: philps-site/components/cart/CartDrawer.tsx

   Responsibilities:
     - Open / close: scrim click, close button, Escape key, body lock
     - Qty stepper + remove via /cart/change.js
     - Section refetch (?section_id=<sidecart-id>) -> inner HTML swap
     - Auto-open after any successful /cart/add submission
     - Public API: window.PhilpsSidecart.open() / close() / refresh()
   ------------------------------------------------------------------ */

(function () {
  const root = document.querySelector('[data-sidecart]');
  if (!root) return;

  const sectionId = root.dataset.sectionId;
  const changeUrl = root.dataset.cartChangeUrl || '/cart/change.js';
  const addUrl = root.dataset.cartAddUrl || '/cart/add.js';

  let loadingCount = 0;

  // ---- Open / close --------------------------------------------------
  function open() {
    if (root.classList.contains('is-open')) return;
    root.classList.add('is-open');
    root.setAttribute('aria-hidden', 'false');
    document.body.classList.add('sidecart-2026-open');
    // Pause Lenis smooth-scroll so the body scroll lock actually holds.
    if (window.lenis && typeof window.lenis.stop === 'function') {
      window.lenis.stop();
    }
    // Focus the drawer for screen readers / Escape handling.
    const drawer = root.querySelector('.sidecart-2026__drawer');
    if (drawer) drawer.focus();
  }

  function close() {
    if (!root.classList.contains('is-open')) return;
    root.classList.remove('is-open');
    root.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('sidecart-2026-open');
    if (window.lenis && typeof window.lenis.start === 'function') {
      window.lenis.start();
    }
  }

  // ---- Loader (ref-counted so overlapping requests don't flicker) ----
  function startLoading() {
    loadingCount += 1;
    const loader = root.querySelector('[data-sidecart-loader]');
    if (loader) loader.hidden = false;
  }
  function endLoading() {
    loadingCount = Math.max(0, loadingCount - 1);
    if (loadingCount === 0) {
      const loader = root.querySelector('[data-sidecart-loader]');
      if (loader) loader.hidden = true;
    }
  }

  // ---- Section refetch -----------------------------------------------
  async function refresh() {
    if (!sectionId) return;
    startLoading();
    try {
      const res = await fetch(
        '/?section_id=' + encodeURIComponent(sectionId),
        { credentials: 'same-origin' },
      );
      if (!res.ok) throw new Error('Section fetch failed');
      const html = await res.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const fresh = doc.querySelector('[data-sidecart-content]');
      const target = root.querySelector('[data-sidecart-content]');
      if (fresh && target) {
        target.innerHTML = fresh.innerHTML;
      }
      // Sync data attributes on the sidecart root from the fresh
      // section — `data-cart-has-paid-shipping` is read by the PDP
      // free-delivery bar to decide whether to render itself, and
      // it can flip when items are added/removed.
      const freshRoot = doc.querySelector('[data-sidecart]');
      if (freshRoot) {
        const flag = freshRoot.getAttribute('data-cart-has-paid-shipping');
        if (flag !== null) {
          root.setAttribute('data-cart-has-paid-shipping', flag);
        }
      }
      // Also update the header cart bubble so the count stays in sync.
      updateCartBubble();
      // Notify other listeners (e.g. the product page's free-delivery
      // bar) that the cart server-state has changed so they can refetch.
      try {
        document.dispatchEvent(new CustomEvent('cart:refreshed'));
      } catch (e) { /* ignore */ }
    } catch (err) {
      // Failsafe — full reload so cart state can't desync visibly.
      console.error('[sidecart] refresh failed', err);
    } finally {
      endLoading();
    }
  }

  async function updateCartBubble() {
    try {
      const res = await fetch('/cart.js', { credentials: 'same-origin' });
      if (!res.ok) return;
      const cart = await res.json();
      const wrap = document.querySelector('#cart-icon-bubble .header__icon-bag-wrap');
      if (!wrap) return;

      const count = cart.item_count || 0;

      // Empty cart — strip the bubble entirely so the SVG sits clean.
      if (count === 0) {
        const existing = wrap.querySelector('.cart-count-bubble');
        if (existing) existing.remove();
        return;
      }

      // Build inner HTML matching the server-rendered structure exactly
      // (see sections/header.liquid). When the count hits 3 digits
      // Shopify hides the visible number to stop it overflowing the
      // pill — we mirror that behaviour here so the layout doesn't
      // shift between SSR and JS-updated states.
      const visible = count < 100 ? '<span aria-hidden="true">' + count + '</span>' : '';
      const sr = '<span class="visually-hidden">' +
        count + ' ' + (count === 1 ? 'item' : 'items') + ' in basket' +
      '</span>';
      const innerHtml = visible + sr;

      let bubble = wrap.querySelector('.cart-count-bubble');
      if (!bubble) {
        // Inject — cart went 0 → N.
        bubble = document.createElement('div');
        bubble.className = 'cart-count-bubble';
        bubble.innerHTML = innerHtml;
        wrap.appendChild(bubble);
      } else {
        // Re-write contents instead of just bumping the digit, so we
        // pick up the >= 100 hide-visible-span branch and keep the
        // screen-reader text in sync with the latest count.
        bubble.innerHTML = innerHtml;
      }
    } catch (err) {
      /* non-fatal */
    }
  }

  // ---- Cart mutation -------------------------------------------------
  async function changeLine(line, quantity) {
    startLoading();
    try {
      const res = await fetch(changeUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        credentials: 'same-origin',
        body: JSON.stringify({ line: line, quantity: quantity }),
      });
      if (!res.ok) throw new Error('Cart change failed');
    } catch (err) {
      console.error('[sidecart] changeLine failed', err);
    } finally {
      endLoading();
      await refresh();
    }
  }

  // ---- Event delegation ---------------------------------------------
  root.addEventListener('click', function (e) {
    // Scrim or explicit close button -> close
    const scrim = e.target.closest('[data-sidecart-scrim]');
    const closeBtn = e.target.closest('[data-sidecart-close]');
    if (scrim || closeBtn) {
      close();
      return;
    }

    // Anchor links that should also close the drawer (Shop pasties,
    // View full basket) — they still navigate normally.
    const closeOnClick = e.target.closest('[data-sidecart-close-on-click]');
    if (closeOnClick) {
      close();
      // don't preventDefault — let the navigation run
      return;
    }

    // Qty steppers
    const qtyBtn = e.target.closest('[data-sidecart-qty]');
    if (qtyBtn) {
      e.preventDefault();
      const line = parseInt(qtyBtn.dataset.lineIndex, 10);
      const delta = parseInt(qtyBtn.dataset.delta, 10);
      if (!line || !delta) return;
      // Find current quantity from the matching <li>. We can't use
      // `closest('[data-line-index]')` here because the button itself
      // carries the same attribute, so closest would resolve to the
      // button and the qty value lookup would miss.
      const item = qtyBtn.closest('.sidecart-2026__item');
      const valueEl = item && item.querySelector('.sidecart-2026__qty-value');
      const current = valueEl ? parseInt(valueEl.textContent, 10) : 1;
      const next = Math.max(0, current + delta);
      changeLine(line, next);
      return;
    }

    // Remove button
    const removeBtn = e.target.closest('[data-sidecart-remove]');
    if (removeBtn) {
      e.preventDefault();
      const line = parseInt(removeBtn.dataset.lineIndex, 10);
      if (!line) return;
      changeLine(line, 0);
      return;
    }
  });

  // Escape key closes the drawer
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && root.classList.contains('is-open')) {
      close();
    }
  });

  // ---- Header cart icon: open the drawer instead of navigating to /cart
  document.addEventListener('click', function (e) {
    const cartIcon = e.target.closest('#cart-icon-bubble');
    if (!cartIcon) return;
    // Allow modified clicks (cmd/ctrl/middle-click) to fall through and
    // navigate to /cart so power users can still open the full page.
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return;
    e.preventDefault();
    open();
  });

  // ---- Auto-open after add-to-cart -----------------------------------
  // Intercept any standard product form submission that posts to the
  // cart_add_url. We submit it ourselves as JSON, then refresh + open
  // the drawer on success. If the form's product page handles its own
  // add-to-cart via JS, that JS should call window.PhilpsSidecart.open()
  // itself.
  function isCartAddForm(form) {
    if (!form || form.tagName !== 'FORM') return false;
    const action = form.getAttribute('action') || '';
    // Match "/cart/add" with or without locale prefix.
    return /\/cart\/add(\b|$)/.test(action);
  }

  document.addEventListener('submit', function (e) {
    const form = e.target;
    if (!isCartAddForm(form)) return;
    // Skip forms owned by a <product-form> custom element — that
    // element already fetches /cart/add itself and will call
    // PhilpsSidecart.refresh() + .open() on success. Handling the
    // submit here as well would double-add the item.
    if (form.closest('product-form')) return;
    e.preventDefault();
    const formData = new FormData(form);
    startLoading();
    open();
    fetch(addUrl, {
      method: 'POST',
      headers: { Accept: 'application/javascript' },
      credentials: 'same-origin',
      body: formData,
    })
      .then(function (res) {
        if (!res.ok) {
          return res.json().then(function (j) {
            throw new Error((j && (j.description || j.message)) || 'Add to cart failed');
          });
        }
        return res.json();
      })
      .then(function () {
        return refresh();
      })
      .catch(function (err) {
        console.error('[sidecart] add failed', err);
        // Fall back to native form submit so the user still sees the
        // /cart page rather than a silent failure.
        form.submit();
      })
      .finally(function () {
        endLoading();
      });
  });

  // ---- Public API ----------------------------------------------------
  window.PhilpsSidecart = {
    open: open,
    close: close,
    refresh: refresh,
  };
})();
